import { useCallback, useState } from "react";
import { useDefaultDeleteModal } from "../../../../../../../../../lib/hooks/defaultDeleteModal";
import {
  createPayer,
  deletePayer,
  editPayer,
} from "../../../../../../../../../acions/accounts/payers";
import { FormInstance } from "antd";
import { IPayerBase } from "../../../../../../../../../types/Payer";
import { useAppDispatch } from "../../../../../../../../../store";
import { useDisableByPermission } from "../../../../../../../../../lib/hooks/useDisableByPermission";

type TButtonHandlers = {
  fetchData: () => void;
  setId: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  parentId?: string;
  form?: FormInstance;
};

export const useButtonHandlers = ({
  fetchData,
  setId,
  form,
  parentId,
}: TButtonHandlers) => {
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useDisableByPermission("ContractPayers", setIsDisabled);

  const setIsOpen = useCallback(
    (val: any) => {
      !val && setId(undefined);
    },
    [setId],
  );

  const { openDeleteModal } = useDefaultDeleteModal(
    deletePayer,
    fetchData,
    setIsOpen,
  );

  const dispatch = useAppDispatch();

  const onDeleteBtnClick = useCallback(
    (id: string) => {
      openDeleteModal(id, "Delete Payer");
    },
    [openDeleteModal],
  );

  const onCancelBtnClick = useCallback(() => {
    setId(undefined);
  }, [setId]);

  const onSuccess = useCallback(
    (isNew = false) => {
      setIsDisabled(false);
      setId(isNew ? null : undefined);

      isNew &&
        form?.setFieldsValue({
          payeeId: undefined,
          status: 0,
        });

      fetchData();
    },
    [form, fetchData, setId],
  );

  const create = useCallback(
    async (req: IPayerBase, isNew: boolean) => {
      try {
        await Promise.resolve(dispatch(createPayer(req)));

        form?.resetFields();
        onSuccess(isNew);
      } catch (error) {
        setIsDisabled(false);
      }
    },
    [dispatch, form, onSuccess],
  );

  const edit = useCallback(
    async (id: string, req: IPayerBase) => {
      try {
        await Promise.resolve(dispatch(editPayer(id, req)));

        onSuccess();
      } catch (error) {
        setIsDisabled(false);
      }
    },
    [dispatch, onSuccess],
  );

  const onSaveBtnClick = useCallback(
    async (isNew: boolean, id: string | null) => {
      try {
        const formData = await form?.validateFields();

        const { assumedDate, soldDate } = formData;

        const req = {
          ...formData,
          contractId: parentId,
          assumedDate: assumedDate
            ? assumedDate.startOf("D").format()
            : undefined,
          soldDate: soldDate ? soldDate.startOf("D").format() : undefined,
        };

        setIsDisabled(true);

        id === null ? create(req, isNew) : edit(id, req);
      } catch (error) {
        setIsDisabled(false);
      }
    },
    [create, edit, form, parentId],
  );

  return { onDeleteBtnClick, onCancelBtnClick, onSaveBtnClick, isDisabled };
};
